export {
  getProductCreateData,
  getProductUpdateData,
  recalculatePosition,
  getUpdatedImages,
  getCustomErrorsFromProductErrors,
};

const MULTILANGUAGE_FIELDS = ["name", "description", "nutrienContents", "ingredients"];

const getCustomErrorsFromProductErrors = (errors, product, availableLocales) => {
  let customErrors;
  const replacementRules = {
    baseUnit: "baseAmountUnit",
    baseAmount: "baseAmountUnit",
    priceCrossed: "price",
  };
  customErrors = renameErrorPaths(errors, replacementRules, product);
  customErrors = combineErrors(customErrors);
  customErrors = mapMultiLanguageFieldErrors(customErrors, availableLocales);
  return customErrors;
};

const combineErrors = errors => {
  return errors.reduce((acc, error) => {
    const index = acc.findIndex(position => position.path[0] == error.path[0]);
    if (index != -1) {
      acc[index].messages.push(error.message);
    } else {
      const newError = { ...error, messages: [error.message] };
      delete newError.message;
      acc.push(newError);
    }
    return acc;
  }, []);
};

const renameErrorPaths = (errors, rules, product) => {
  return errors.reduce((acc, error) => {
    const applicableRules = { ...rules };
    const priceIndex = error.path[0].match(/variants\.(\d)\.price/)?.[1];
    if (priceIndex) {
      applicableRules["price"] = product.variants[priceIndex].priceSale ? "priceSale" : "price";
    }

    const newError = { ...error };

    for (let [replaceable, replacement] of Object.entries(applicableRules)) {
      if (error.path[0].match(replaceable)) {
        newError.path = [newError.path[0].replace(replaceable, replacement)];
        break;
      }
    }
    return [...acc, newError];
  }, []);
};

const mapMultiLanguageFieldErrors = (errors, locales) => {
  return errors.reduce((acc, error) => {
    if (MULTILANGUAGE_FIELDS.some(field => error.path[0] == field)) {
      const mappedErrors = locales.map(locale => {
        const field = error.path[0];
        const mappedError = JSON.parse(JSON.stringify(error));

        mappedError.path[0] = `${field}_${locale}`;

        return mappedError;
      });
      return [...acc, ...mappedErrors];
    } else {
      return [...acc, error];
    }
  }, []);
};

const getVariantData = variant => {
  return {
    price: variant.priceSale ? variant.priceSale : variant.price,
    priceCrossed: variant.priceSale ? variant.price : null,
    baseUnit: variant.baseUnit,
    baseAmount: parseInt(variant.baseAmount),
    baseAmountProduct: parseFloat(variant.baseAmountProduct),
    tradeId: variant.tradeId,
    properties: variant.properties,
    countOnHand: variant.countOnHand,
    mpn: variant.mpn,
    uniqueId: variant.uniqueId,
    images: getImages(variant.images),
    ...(variant.id ? { id: variant.id } : {}),
  };
};

const getImages = images =>
  images
    .filter(image => !image.deleted)
    .map((image, index) => {
      const { attachment, id, persisted } = image;
      return { attachment, id: persisted ? id : undefined, position: index };
    });

const getProductCreateData = product => {
  return {
    ageGroup: product.ageGroup,
    ageRestriction: product.ageRestriction,
    boostSort: product.boostSort,
    brand: product.brand,
    description: product.description,
    doesSubsidize: product.doesSubsidize,
    gender: product.gender,
    ingredients: product.ingredients,
    nutrienContents: product.nutrienContents,
    preorderDays: product.preorderDays,
    serviceIds: product.serviceIds,
    subsidyAmount: product.subsidyAmount,
    taxCategoryId: product.taxCategoryId,
    name: product.name,
    vendorId: product.vendorId,
    taxonDatabaseIds: product.taxonIds,
    shippingMethodIds: product.shippingMethodIds,
    variants: product.variants?.map(getVariantData),
    images: getImages(product.images),
    active: product.active,
  };
};

const getProductUpdateData = product => {
  return {
    ageGroup: product.ageGroup,
    ageRestriction: product.ageRestriction,
    boostSort: product.boostSort,
    brand: product.brand,
    description: product.description,
    doesSubsidize: product.doesSubsidize,
    gender: product.gender,
    ingredients: product.ingredients,
    nutrienContents: product.nutrienContents,
    preorderDays: product.preorderDays,
    serviceIds: product.serviceIds,
    subsidyAmount: product.subsidyAmount,
    taxCategoryId: product.taxCategoryId,
    id: product.id,
    name: product.name,
    vendorId: product.vendorId,
    taxonDatabaseIds: product.taxonIds,
    shippingMethodIds: product.shippingMethodIds,
    variants: product.variants?.map(getVariantData),
    images: getImages(product.images),
    active: product.active,
  };
};

const recalculatePosition = images => {
  return [...images].map((element, idx) => ({
    ...element,
    position: idx,
  }));
};

const getUpdatedImages = (storeImages, { file, preview }) => {
  let image, images;
  const existingIndex = storeImages.findIndex(image => image.id == file.id);
  const alreadyExists = existingIndex != -1;
  const isThumbnail = !!preview;

  if (alreadyExists) {
    image = storeImages[existingIndex];
  } else {
    image = { persisted: false, id: file.id };
  }

  if (isThumbnail) {
    image.url = preview;
  } else {
    image.attachment = {
      id: file.meta["key"].match(/^cache\/(.+)/)[1],
      storage: "cache",
      metadata: {
        size: file.size,
        filename: file.name,
        mime_type: file.type,
      },
    };
  }

  if (alreadyExists) {
    storeImages[existingIndex] = image;
    images = storeImages;
  } else {
    images = [...storeImages, image];
  }

  return images;
};
